/**
 * Words for the send sheet's "before sending" check on client-visible text:
 * where the flagged text is ("the helper text on question 2") and what the
 * client would see.
 */
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeLintIssue
} from "@/api/types/outgoingIntake";
import { intakeSteps } from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";

const contains = (node: OutgoingIntakeFormNode, id: string): boolean =>
  String(node.node_id) === id ||
  (node.children ?? []).some((c) => contains(c, id));

/** The number the client sees for the question holding `nodeId`. */
export const questionNumberOf = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string | null
): number | null => {
  if (!nodeId) return null;
  const index = intakeSteps(schema).findIndex((s) => contains(s, nodeId));
  return index >= 0 ? index + 1 : null;
};

const findSection = (
  nodes: OutgoingIntakeFormNode[],
  id: string
): OutgoingIntakeFormNode | null =>
  nodes.reduce<OutgoingIntakeFormNode | null>((found, node) => {
    if (found || node.node_type !== "section") return found;
    return String(node.node_id) === id
      ? node
      : findSection(node.children ?? [], id);
  }, null);

/** "The helper text on question 2", "The helper text on the “Travel”
 * section", "The note", "The title". */
export const issuePlace = (
  issue: OutgoingIntakeLintIssue,
  schema: OutgoingIntakeFormNode[]
): string => {
  if (issue.field === "note") return "The note to the client";
  if (issue.key === "title") return "The title";
  const section = issue.node_id ? findSection(schema, issue.node_id) : null;
  if (section) {
    const name = String(section.title ?? "").trim();
    return `The ${issue.where} on ${
      name ? `the “${name}” section` : "a section"
    }`;
  }
  const n = questionNumberOf(schema, issue.node_id);
  return `The ${issue.where}${n ? ` on question ${n}` : ""}`;
};

export const clip = (text: string, max = 110): string =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

export const lintHeadline = (open: number): string =>
  `${open} ${open === 1 ? "thing" : "things"} to fix before sending`;
